import { useCallback, useRef, useState } from 'react';

export type UploadTask = {
  file: File;
  // Signal is aborted on cancel — wire it into your request's own abort
  // (RTK Query's mutation trigger exposes `.abort()`).
  upload: (signal: AbortSignal) => Promise<unknown>;
};

export type UploadFileStatus = 'pending' | 'uploading' | 'success' | 'error';

export type UploadFileState = {
  file: File;
  status: UploadFileStatus;
  error?: string;
};

export type UploadPhase = 'idle' | 'uploading' | 'done' | 'cancelled';

export type UploadState = {
  phase: UploadPhase;
  files: UploadFileState[];
  completed: number;
  total: number;
  currentFileName: string | null;
  startedAt: number | null;
  errorCount: number;
  successCount: number;
};

const INITIAL_STATE: UploadState = {
  phase: 'idle',
  files: [],
  completed: 0,
  total: 0,
  currentFileName: null,
  startedAt: null,
  errorCount: 0,
  successCount: 0,
};

const extractErrorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const anyErr = err as { data?: { error?: string; message?: string }; error?: string; status?: number };
    return (
      anyErr.data?.error ||
      anyErr.data?.message ||
      anyErr.error ||
      (anyErr.status ? `HTTP ${anyErr.status}` : 'Upload failed')
    );
  }
  return 'Upload failed';
};

export const useMultiFileUpload = () => {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const cancelledRef = useRef(false);

  const start = useCallback(async (tasks: UploadTask[]): Promise<UploadState> => {
    cancelledRef.current = false;
    const workingFiles: UploadFileState[] = tasks.map((t) => ({
      file: t.file,
      status: 'pending',
    }));
    setState({
      phase: 'uploading',
      files: workingFiles.map((f) => ({ ...f })),
      completed: 0,
      total: tasks.length,
      currentFileName: null,
      startedAt: Date.now(),
      errorCount: 0,
      successCount: 0,
    });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < tasks.length; i++) {
      if (cancelledRef.current) break;
      const task = tasks[i];
      const controller = new AbortController();
      abortRef.current = controller;

      workingFiles[i] = { ...workingFiles[i], status: 'uploading' };
      setState((s) => ({
        ...s,
        currentFileName: task.file.name,
        files: s.files.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading' } : f,
        ),
      }));

      try {
        await task.upload(controller.signal);
        successCount += 1;
        workingFiles[i] = { ...workingFiles[i], status: 'success' };
        setState((s) => ({
          ...s,
          completed: s.completed + 1,
          successCount: s.successCount + 1,
          files: s.files.map((f, idx) =>
            idx === i ? { ...f, status: 'success' } : f,
          ),
        }));
      } catch (err) {
        console.error('[useMultiFileUpload] file failed', {
          name: task.file.name,
          size: task.file.size,
          type: task.file.type,
          error: err,
        });
        const message = extractErrorMessage(err);
        errorCount += 1;
        workingFiles[i] = {
          ...workingFiles[i],
          status: 'error',
          error: message,
        };
        setState((s) => ({
          ...s,
          completed: s.completed + 1,
          errorCount: s.errorCount + 1,
          files: s.files.map((f, idx) =>
            idx === i ? { ...f, status: 'error', error: message } : f,
          ),
        }));
      }
    }
    abortRef.current = null;

    const finalPhase: UploadPhase = cancelledRef.current ? 'cancelled' : 'done';
    setState((s) => ({ ...s, phase: finalPhase, currentFileName: null }));

    return {
      phase: finalPhase,
      files: workingFiles,
      completed: successCount + errorCount,
      total: tasks.length,
      currentFileName: null,
      startedAt: null,
      errorCount,
      successCount,
    };
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = false;
    abortRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  return { state, start, cancel, reset };
};
